/**
 * The codec between a Market Intelligence surface's live control state and the
 * `config` object a saved view stores.
 *
 * One place, three readers: the surfaces render from a config, the view bar
 * compares live state against a saved view through it, and applying a view
 * produces one. Keeping all three on one codec is what makes "is this dirty"
 * answerable at all — the alternative is each surface hand-rolling a
 * comparison and the unsaved dot lying in a different way on each screen.
 *
 * ## Two rules that the whole thing rests on
 *
 * **Every field parses with a fallback, and the fallback is the surface's
 * shipped default.** A stored config may predate a control, come from a
 * hand-edited localStorage entry, or arrive from an older build — so
 * `configFromStored` never throws and never yields a half-state. The defaults
 * are asserted against the server's own system views (the generated
 * `MARKET_SYSTEM_VIEW_CONFIGS`) in `view-config.test.ts`, because a drift
 * there is the nasty failure the grid-view registries warn about: a surface
 * that boots **permanently dirty**, showing an unsaved-changes dot the reader
 * can never clear because Reset returns them somewhere that never compares
 * equal to what loaded.
 *
 * **A value equal to its default is OMITTED from the stored object.** So the
 * default state stores `{}`, and `configToStored(configFromStored(x))` is a
 * fixed point. Without this the dirty compare would depend on how a reader
 * arrived at a state rather than on the state itself — and the server's
 * shipped defaults, which are all `{}`, would never compare equal.
 *
 * ## There are no URL params here
 *
 * An earlier cut kept every surface's control state in the query string. That
 * was wrong for a structural reason: the six surfaces share one query string
 * and `SubTabs` carries it across navigations, so each surface read and wrote
 * a namespace five others could see. See `view-prefs.ts`, which replaced it
 * with per-surface storage, for the failure modes that produced.
 *
 * ## The navigator window is stored as absolute dates
 *
 * `RangeNavigator` models its window as two fractions of the loaded series
 * (`{start: 0.52, end: 0.78}`), which is the right model for a brush but the
 * wrong one to persist: as new assessments arrive the same fraction pair
 * addresses a different calendar range every day, so a saved view would drift.
 *
 * So the stored form is `from` / `to` as `yyyy-MM-dd`, and the fraction ↔ date
 * conversion happens against the loaded series at render time
 * (`windowFromDates` / `datesFromWindow` below). A window that falls outside
 * the currently loaded history clamps to the extent rather than disappearing —
 * which is what happens when a reader saves a 3Y-range window and then
 * switches the history selector to 3M.
 *
 * ## …except on the forward curve, where it ROLLS (AB#8508)
 *
 * The curve's axis is delivery month and its front edge moves every month —
 * it always starts at next month. An absolute window there expires: a view
 * saved as "Oct 2026 – Sep 2027" and opened in December would silently shrink,
 * and one whose months had all passed would show nothing. So the curve saves
 * `curveYears` — calendar years as OFFSETS from this year, exactly as
 * PowerUp 1.0's year slider did — and rolls forward each January: "this
 * year + 1 through + 3" is 2027–2029 all of 2026, then 2028–2030. Offset 0,
 * the current year, means next month through December. `rangeFromCurveYears`
 * resolves it against the loaded curve at render time.
 *
 * The chart's own range slider is a zoom there, NOT saved: two saved windows
 * over one axis would disagree, and the view would never stop reading dirty.
 */

import { MAX_COMPARE } from "@/lib/market-intelligence/compare";
import {
  DEFAULT_DATA_TABLE,
  dataTableToStored,
  parseDataTablePrefs,
  type DataTablePrefs,
} from "@/lib/market-intelligence/data-table-prefs";
import { STRIP_TENORS } from "@/lib/market-intelligence/tenors";
import {
  MARKET_SYSTEM_VIEW_CONFIGS,
  type MarketViewScope,
} from "@/types/market-views";

import type { SeriesPoint } from "@/components/market-intelligence/charts/chart-data";
import type { NavWindow } from "@/components/market-intelligence/charts/navigator-model";
import type { HourlyWindow } from "@/pages/market-intelligence/hubs/index/hourly-model";
import type { PeakBlock, StripTenor } from "@/types/market-intelligence";

// ── The control vocabularies ──────────────────────────────────────────────────

/** History spans the Strips view offers. */
export const STRIP_RANGES = ["3m", "1y", "3y", "max"] as const;
export type StripRangeKey = (typeof STRIP_RANGES)[number];

/** Days of history each range asks the server for. */
export const STRIP_RANGE_DAYS: Record<StripRangeKey, number> = {
  "3m": 90,
  "1y": 365,
  "3y": 1095,
  max: 1460,
};

/**
 * The history spans the REC surfaces offer, and what each asks the server for.
 *
 * A subset of `STRIP_RANGES` reusing the same keys, so `config.range` stays one
 * field with one vocabulary — but the DAYS differ, because certificates are a
 * different kind of series. Strips are assessed daily and four years is deep
 * history; certificates are assessed weekly and the mirror holds 2013, so
 * `STRIP_RANGE_DAYS.max` (1460) would still hide a decade.
 *
 * **`max` is `null`, not a number.** It means "send no `from` at all" rather
 * than a very large window: the server's own read is unbounded without one, so
 * a sentinel here would be this file guessing how far back the subscription
 * goes, and that guess would silently clip the day the mirror gains an older
 * assessment.
 */
export const REC_RANGE_DAYS: Record<StripRangeKey, number | null> = {
  "3m": 90,
  "1y": 365,
  "3y": 1095,
  max: null,
};

/** The years a calendar or quarterly strip can be struck for. */
export const STRIP_YEARS = [2027, 2028, 2029, 2030] as const;
export const QUARTERS = [1, 2, 3, 4] as const;
export type Quarter = 1 | 2 | 3 | 4;

/** Hourly's History options, shortest first. */
export const HOURLY_WINDOWS = ["1d", "3d", "7d", "14d", "30d"] as const;


/** Index Trends' time interval: monthly averages, or daily ones. */
export const TREND_INTERVALS = ["monthly", "daily"] as const;
export type TrendInterval = (typeof TREND_INTERVALS)[number];

/**
 * Index Trends' time-series mode: one continuous timeline, or each year as its
 * own line on a January–December axis. Yearly is monthly-only.
 */
export const TREND_MODES = ["continuous", "yearly"] as const;
export type TrendMode = (typeof TREND_MODES)[number];

/**
 * Index Trends' market types, in canonical order. `dart` is the DART spread,
 * day-ahead MINUS real-time — PowerUp 1.0's sign.
 */
export const TREND_MARKETS = ["da", "rt", "dart"] as const;
export type TrendMarket = (typeof TREND_MARKETS)[number];

/**
 * Day-ahead and real-time: the Market chips of a chart that has no DART to
 * offer (Hourly, which settles none; Basis, a zone less its hub).
 */
export const SETTLED_MARKETS = ["da", "rt"] as const;
export type SettledMarket = (typeof SETTLED_MARKETS)[number];

/** Narrows a saved market list to day-ahead and real-time, in chip order; both when neither is picked. */
export function settledMarketsOf(markets: readonly string[]): SettledMarket[] {
  const picked = SETTLED_MARKETS.filter((m) => markets.includes(m));
  return picked.length > 0 ? picked : [...SETTLED_MARKETS];
}

/**
 * Index Trends' Yearly range: calendar years as OFFSETS from this year —
 * `{ from: -5, to: 0 }` is the last six years, and rolls forward each
 * January the way the forward curve's `CurveYears` does. Never positive: an
 * index settles the past.
 */
export interface TrendYears {
  from: number;
  to: number;
}

/**
 * The block vocabulary, in canonical order.
 *
 * Exported because the block chips normalise Radix's selection order through
 * `parsePeaks` the same way the tenor chips do — the control and the codec
 * must not disagree about ordering, or two identical selections compare
 * unequal and the saved view shows a dirty dot that will not clear.
 */
export const PEAK_BLOCKS: readonly PeakBlock[] = ["atc", "on_peak", "off_peak"];

// ── The saved shape ───────────────────────────────────────────────────────────

/**
 * The navigator's window as absolute dates — `null` when the reader has not
 * brushed, meaning "the full extent of whatever history is loaded".
 *
 * Not a fraction pair; see the module comment.
 */
export interface TimeRange {
  /** `yyyy-MM-dd`. */
  from: string;
  /** `yyyy-MM-dd`. */
  to: string;
}

/**
 * The forward curve's window as calendar years, inclusive, each an OFFSET from
 * this year — `{ from: 1, to: 3 }` is "next year through three years out".
 * Offset 0 is the current year, which on a curve starting next month means
 * next month through December. See the module comment for why the curve rolls
 * where every other surface stores dates.
 */
export interface CurveYears {
  from: number;
  to: number;
}

/**
 * The forward curve's window as delivery months, inclusive, each an OFFSET
 * from the curve's prompt month — `{ from: 0, to: 11 }` is "the next twelve
 * months". It rolls the way `CurveYears` does, but a month at a time: a view
 * saved as Jan 2027 – Dec 2028 in October still covers months 3–26 out in
 * November. Replaces `CurveYears`, which is read only for views saved before
 * the year slider became a month range.
 */
export interface CurveMonths {
  from: number;
  to: number;
}

/**
 * Every control any surface can save, as one flat shape.
 *
 * Flat and total rather than a discriminated union per scope, deliberately:
 * the dirty compare, the storage codec and the apply path all want to walk "the
 * fields this scope owns" generically, and a union would make each of them a
 * switch that a new surface has to be added to in three places. `FIELDS_BY_SCOPE`
 * below is the single table that says which fields a scope actually owns, and
 * everything else reads it.
 */
export interface MarketViewConfig {
  /** Rolling tenors — never empty. */
  tenors: readonly StripTenor[];
  /** Calendar / quarterly years — never empty. */
  years: readonly number[];
  /** Quarterly strips' quarters — never empty, Q1 to Q4. Each crosses every year. */
  quarters: readonly Quarter[];
  /**
   * Blocks to draw — never empty.
   *
   * A LIST rather than a scalar: on the Strips view each selected block
   * crosses with each selected tenor/year to make its own line, and on the
   * Curve view each is a line of its own. Defaults to ATC alone, so an
   * untouched surface draws exactly what it drew when this was an either/or
   * toggle.
   */
  peaks: readonly PeakBlock[];
  /** RECs' history span. (Strips used to save one; they now load MAX.) */
  range: StripRangeKey;
  /** Hourly's History — how many recent days of hours to show. */
  window: HourlyWindow;
  /**
   * Capacity zones to draw, or EMPTY meaning "whatever the server selected".
   *
   * The sentinel is load-bearing. Every other default in this file is a
   * constant, but the capacity page's opening zone set is computed per market
   * by the server (`default_zone_codes` — the pool plus the widest
   * divergences), so there is no literal to compare against here. Storing the
   * resolved list would make a freshly-loaded page differ from its own
   * default the moment the server's pick changed, and the reader would get an
   * unsaved-changes dot they could never clear.
   *
   * So the default is `[]`, which projects to `{}` like every other
   * surface's, and the page resolves `[]` to the server's list at render.
   * A non-empty value is always a deliberate user selection.
   */
  zones: readonly string[];
  /**
   * Capacity zones' counterpart for REC certificates, and empty means the
   * same thing: draw whatever the market publishes. The server decides which
   * certificates a region has, so there is no constant here to compare a
   * resolved list against — see `zones`.
   */
  certificates: readonly string[];
  /** The navigator brush, or `null` for the full extent. */
  timeRange: TimeRange | null;
  /** Index Trends: monthly or daily averages. */
  interval: TrendInterval;
  /** Index Trends: one timeline, or a line per year. */
  seriesMode: TrendMode;
  /** Index Trends and Hourly: which markets to draw — never empty. Hourly draws no DART. */
  markets: readonly TrendMarket[];
  /** Index Trends' Yearly range, rolling — or `null` for the last six years. */
  trendYears: TrendYears | null;
  /**
   * The forward curve's year range, rolling — or `null` for the whole curve.
   * The curve's counterpart to `timeRange`, which it does not own.
   */
  curveYears: CurveYears | null;
  /** The forward curve's month range, rolling — or `null` for the whole curve. */
  curveMonths: CurveMonths | null;
  /**
   * Other points drawn on this hub's chart, in the order they were added —
   * which is also the colour order, so it is kept, not sorted. Empty, the
   * default, is the hub alone. See `lib/market-intelligence/compare.ts`.
   */
  compare: readonly string[];
  /**
   * The Data dialog's layout — its tables' columns, their order and their
   * sort. Owned by every scope (every chart has the dialog), and changes no
   * filter. See `data-table-prefs.ts`.
   */
  dataTable: DataTablePrefs;
}

/** Which of `MarketViewConfig`'s fields each surface actually owns. */
const FIELDS_BY_SCOPE: Record<MarketViewScope, readonly (keyof MarketViewConfig)[]> = {
  // Tenor chips, block and the brush. No `range`: every strip loads its
  // whole history (`STRIP_RANGE_DAYS.max`) and the Range fields narrow it.
  forwards_strips_rolling: ["tenors", "peaks", "timeRange", "compare"],
  // Years instead of tenors.
  forwards_strips_calendar: ["years", "peaks", "timeRange", "compare"],
  // Years plus which quarters of each.
  forwards_strips_quarterly: ["years", "quarters", "peaks", "timeRange", "compare"],
  // The curve chart is one assessment's shape, so it has no history span and
  // its x-axis is delivery month. Its saved window is a ROLLING month range
  // rather than the brush's dates (see the module comment); `curveYears` is
  // still read for a view saved while it was a year range.
  forwards_curve: ["peaks", "curveYears", "curveMonths", "compare"],
  // Interval, mode, products (`peaks`), markets and the range — the month or
  // day window in Continuous, the rolling years in Yearly.
  index_trends: [
    "timeRange",
    "peaks",
    "markets",
    "interval",
    "seriesMode",
    "trendYears",
    "compare",
  ],
  // Hourly's History bounds which hours are shown; the Range brushes within it.
  // The one hub chart that kept a History toggle — a month of hours is all a
  // chart can usefully hold, where the strips load their whole history. Its
  // Market chips are Trends' less DART, which hourly settlements do not carry.
  index_hourly: ["window", "markets", "timeRange", "compare"],
  // A zone less its hub, monthly: which markets, and the Range of months.
  // No Compare: a basis is already a pairing of two points.
  index_basis: ["markets", "timeRange"],
  // Zone chips plus the brush. No `range`: an auction publishes every
  // delivery year at once, so there is no history span to fetch.
  region_capacity: ["zones", "timeRange"],
  // Certificate chips, the history span, and the brush.
  //
  // This used to omit `range`, on the grounds that "the series arrives whole".
  // It does not: `/api/market/recs/series` applies a 400-day default when the
  // request sends no `from`, so the page was clipped to about 57 weekly
  // assessments while the mirror holds history back to 2013. `range` is the
  // level that decides how much is FETCHED; `timeRange` is the brush within
  // it. Deriving the request bound from the brush instead cannot reach further
  // back than what is already loaded — see `recsFrom`.
  region_recs: ["certificates", "range", "timeRange"],
};

/** A scope's fields, plus the Data dialog's layout, which every chart has. */
export function fieldsForScope(
  scope: MarketViewScope,
): readonly (keyof MarketViewConfig)[] {
  return [...FIELDS_BY_SCOPE[scope], "dataTable"];
}

/**
 * The shipped defaults — what an untouched surface shows, and what Reset
 * returns to.
 *
 * Asserted against the server's `MARKET_SYSTEM_VIEW_CONFIGS` in the tests.
 * `timeRange: null` everywhere: the default brush is the full extent of the
 * loaded series, which is not a fixed pair of dates.
 */
export const DEFAULT_CONFIG: MarketViewConfig = {
  tenors: STRIP_TENORS,
  // One year, not four: the calendar chart is a comparison the reader opts
  // into, and four lines by default would bury the near-dated strip most
  // quotes actually reference.
  years: [STRIP_YEARS[0]],
  quarters: [3],
  // ATC alone, not all three: crossing blocks with tenors multiplies the
  // lines, so the comparison is opt-in exactly as the calendar years are.
  peaks: ["atc"],
  range: "1y",
  // A month of hours: enough to see the shape across weeks, few enough to read.
  window: "30d",
  // "Whatever the server selected" — see each field's own note.
  zones: [],
  certificates: [],
  timeRange: null,
  curveYears: null,
  curveMonths: null,
  interval: "monthly",
  seriesMode: "continuous",
  // Day-ahead and real-time, like PowerUp 1.0; the spread is opt-in.
  markets: ["da", "rt"],
  trendYears: null,
  // The hub alone; comparing is opt-in.
  compare: [],
  // Every column, in the table's own order and sort.
  dataTable: DEFAULT_DATA_TABLE,
};

// ── Parsing a stored config ───────────────────────────────────────────────────

/**
 * Normalises a set of tenor values into canonical order.
 *
 * Radix hands back SELECTION order, so clicking 60 then 12 would otherwise
 * produce `[60, 12]` — reversing the chart's series and the statistics'
 * columns relative to the chips, and making two identical selections compare
 * unequal. Unknown values are dropped, which is what makes a stale or
 * hand-edited stored config safe.
 *
 * Exported because the controls normalise Radix's output through it too, so
 * the control and the codec cannot disagree about ordering.
 */
export function parseTenors(values: readonly string[]): StripTenor[] {
  const wanted = new Set(values.map(Number));
  return STRIP_TENORS.filter((t) => wanted.has(t));
}

/** Market types in canonical order, unknowns dropped — see `parseTenors`. */
export function parseMarkets(values: readonly string[]): TrendMarket[] {
  const wanted = new Set(values);
  return TREND_MARKETS.filter((m) => wanted.has(m));
}

/** A Yearly offset pair: whole numbers, not in the future, ordered. */
function parseTrendYears(from: unknown, to: unknown): TrendYears | null {
  if (from === undefined || to === undefined) return null;
  const a = Number(from);
  const b = Number(to);
  if (!Number.isInteger(a) || !Number.isInteger(b) || b > 0 || a > b) return null;
  return { from: a, to: b };
}

/** The same normalisation for blocks, so chip order never varies. */
export function parsePeaks(values: readonly string[]): PeakBlock[] {
  const wanted = new Set(values);
  return PEAK_BLOCKS.filter((b) => wanted.has(b));
}

/** Quarters, de-duplicated and in order, unknowns dropped — see `parseTenors`. */
export function parseQuarters(values: readonly string[]): Quarter[] {
  const wanted = new Set(values.map(Number));
  return QUARTERS.filter((q) => wanted.has(q));
}

/** Calendar/quarterly years, de-duplicated and ascending. */
export function parseYears(values: readonly string[]): number[] {
  // Shape only, not membership. Which years a hub quotes is live data — the
  // chips come from `useStripPeriods`, and `StripsView`'s `activeYears`
  // narrows the selection to them. Filtering against `STRIP_YEARS` here
  // silently dropped every chip outside 2027–2030: Cal 31 or a quarterly 26
  // lit nothing when clicked, and a saved view holding one lost it on load.
  const years = new Set<number>();
  for (const value of values) {
    const year = Number(value);
    if (Number.isInteger(year) && year >= 2000 && year <= 2100) years.add(year);
  }
  return [...years].sort((a, b) => a - b);
}

/**
 * Zone codes, de-duplicated and in a stable order.
 *
 * Unlike tenors and blocks there is no canonical vocabulary to filter
 * against — the zone list is per-market wire data, and this module must not
 * grow a copy of it. So it normalises what it CAN: drops blanks and repeats,
 * then sorts, which is enough to make two identical selections compare equal
 * regardless of the order Radix handed them back in.
 *
 * A code the market no longer publishes survives here and is simply not
 * matched by any series — the page draws what it can and ignores the rest,
 * which is how a saved view degrades rather than breaks.
 */
/**
 * The `from` a REC series request should carry for a chosen history span.
 *
 * `null` means send no `from`, which is how `max` reaches the whole mirror
 * (see [`REC_RANGE_DAYS`]).
 *
 * **Derived from the range, never from the brush.** The brush is a window over
 * the points already in hand, so a bound taken from it can only ever move
 * forward: `datesFromWindow` returns a date drawn from the loaded series, so
 * `from` would be `>= ` the first loaded point, and each refetch would narrow
 * the series again. History is the level that decides how much is fetched;
 * the brush selects within it.
 */
export function recsFrom(range: StripRangeKey, today: Date): string | null {
  const days = REC_RANGE_DAYS[range];
  if (days === null) return null;
  const from = new Date(today.getTime() - days * 86_400_000);
  return from.toISOString().slice(0, 10);
}

/**
 * Compared point codes: blanks and repeats dropped, capped, ORDER KEPT — the
 * order is the colour order, so two lists in different orders are genuinely
 * different charts. Like zones there is no vocabulary to filter against here;
 * the chart skips a code it cannot draw (`resolveCompared`).
 */
export function parseCompare(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const codes: string[] = [];
  for (const value of raw) {
    if (typeof value !== "string") continue;
    const code = value.trim();
    if (code.length > 0 && !codes.includes(code)) codes.push(code);
    if (codes.length >= MAX_COMPARE) break;
  }
  return codes;
}

export function parseZones(values: readonly string[]): string[] {
  return [...new Set(values.map((v) => v.trim()).filter((v) => v.length > 0))].sort();
}

/** `yyyy-MM-dd` and a real calendar date, or `null`. */
function parseIsoDate(raw: unknown): string | null {
  if (typeof raw !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const ms = Date.parse(`${raw}T00:00:00Z`);
  return Number.isFinite(ms) ? raw : null;
}

/**
 * A `from`/`to` pair, or `null` unless BOTH sides are valid and ordered.
 *
 * Half a window is not a window: a lone `from` would leave the other edge
 * undefined and the brush would have to invent it, so a partial pair is
 * discarded in favour of the full extent.
 */
function parseTimeRange(from: unknown, to: unknown): TimeRange | null {
  const start = parseIsoDate(from);
  const end = parseIsoDate(to);
  if (!start || !end || start > end) return null;
  return { from: start, to: end };
}

/**
 * A year-offset pair, or `null` unless both are whole numbers, this year (0) or
 * later, and ordered. Half a window is not a window — see `parseTimeRange`.
 */
function parseCurveYears(from: unknown, to: unknown): CurveYears | null {
  if (from === undefined || to === undefined) return null;
  const a = Number(from);
  const b = Number(to);
  if (!Number.isInteger(a) || !Number.isInteger(b) || a < 0 || b < a) return null;
  return { from: a, to: b };
}

function parseOneOf<T extends string>(
  raw: unknown,
  allowed: readonly T[],
  fallback: T,
): T {
  return typeof raw === "string" && allowed.includes(raw as T)
    ? (raw as T)
    : fallback;
}

/** Numbers out of a stored array, tolerating strings and junk entries. */
function numbersFrom(raw: unknown): string[] {
  return Array.isArray(raw) ? raw.map(String) : [];
}

/**
 * Reads a surface's config out of a stored object — a saved view's `config`,
 * or a persisted localStorage snapshot.
 *
 * **Fields the scope does not own keep their default.** That is what keeps a
 * config from carrying meaning across surfaces: `peak` exists on both Strips
 * and Curve, so reading a Strips config under the Curve scope must not import
 * its tenor set, and vice versa. It is also the last line of defence now that
 * each surface has its own storage key — belt and braces, since the keys
 * already make a cross-surface read impossible.
 *
 * Never throws. Every field falls back to the shipped default, so a config
 * written by an older build, or edited by hand, degrades one field at a time
 * instead of breaking the page.
 */
export function configFromStored(
  scope: MarketViewScope,
  stored: Record<string, unknown>,
): MarketViewConfig {
  const owns = new Set(fieldsForScope(scope));
  const config: MarketViewConfig = { ...DEFAULT_CONFIG };

  if (owns.has("tenors")) {
    const tenors = parseTenors(numbersFrom(stored.tenors));
    // Never empty: an empty set is a blank chart with no chip lit to click
    // back, so it degrades to the full default rather than to nothing.
    if (tenors.length > 0) config.tenors = tenors;
  }
  if (owns.has("years")) {
    const years = parseYears(numbersFrom(stored.years));
    if (years.length > 0) config.years = years;
  }
  if (owns.has("quarters")) {
    // Tolerates the scalar `quarter` a view saved while the Quarter chips
    // were single-select, as `peaks` tolerates `peak`: ignoring it would
    // reset a saved Q1 view to the default Q3.
    const raw = Array.isArray(stored.quarters) ? stored.quarters : [stored.quarter];
    const quarters = parseQuarters(raw.map(String));
    if (quarters.length > 0) config.quarters = quarters;
  }
  if (owns.has("peaks")) {
    // Tolerates the scalar `peak` a view saved while blocks were an either/or
    // toggle. Those rows are live in sandbox, and ignoring the old key would
    // silently reset an existing saved view to ATC — the reader would open a
    // view they had saved on On-peak and find it showing something else.
    const raw = Array.isArray(stored.peaks)
      ? stored.peaks.map(String)
      : typeof stored.peak === "string"
        ? [stored.peak]
        : [];
    const peaks = parsePeaks(raw);
    // Never empty, for the same reason the tenor set is not: an empty
    // selection is a blank chart with no chip lit to click back.
    if (peaks.length > 0) config.peaks = peaks;
  }
  if (owns.has("range")) {
    config.range = parseOneOf(stored.range, STRIP_RANGES, DEFAULT_CONFIG.range);
  }
  if (owns.has("window")) {
    config.window = parseOneOf(stored.window, HOURLY_WINDOWS, DEFAULT_CONFIG.window);
  }
  if (owns.has("certificates")) {
    // Empty is meaningful here too — "every certificate the region has" —
    // so there is no never-empty degrade.
    config.certificates = parseZones(
      Array.isArray(stored.certificates) ? stored.certificates.map(String) : [],
    );
  }
  if (owns.has("zones")) {
    // No "never empty" degrade here, unlike tenors and peaks: for this field
    // empty is a MEANINGFUL value — "use the server's selection" — not the
    // blank chart those guards exist to prevent.
    config.zones = parseZones(
      Array.isArray(stored.zones) ? stored.zones.map(String) : [],
    );
  }
  if (owns.has("timeRange")) {
    // Stored FLAT as `from` / `to` rather than nested, so a stored row reads
    // as a plain bag of scalars and the server never has to know the shape.
    config.timeRange = parseTimeRange(stored.from, stored.to);
  }
  if (owns.has("interval")) {
    config.interval = parseOneOf(stored.interval, TREND_INTERVALS, DEFAULT_CONFIG.interval);
  }
  if (owns.has("seriesMode")) {
    config.seriesMode = parseOneOf(stored.seriesMode, TREND_MODES, DEFAULT_CONFIG.seriesMode);
  }
  if (owns.has("markets")) {
    const markets = parseMarkets(Array.isArray(stored.markets) ? stored.markets.map(String) : []);
    // Never empty, like tenors and blocks: nothing lit is a blank chart.
    if (markets.length > 0) config.markets = markets;
  }
  if (owns.has("trendYears")) {
    config.trendYears = parseTrendYears(stored.trendYearsFrom, stored.trendYearsTo);
  }
  if (owns.has("compare")) {
    config.compare = parseCompare(stored.compare);
  }
  if (owns.has("curveYears")) {
    // A curve view saved before AB#8508 stored `from`/`to` dates. Those are
    // not owned any more, so they are ignored and the curve opens whole
    // rather than on a window that has since expired. (No saved view was
    // converted: the surface had no production users when it changed.)
    config.curveYears = parseCurveYears(stored.yearsFrom, stored.yearsTo);
  }
  if (owns.has("curveMonths")) {
    // Offsets from the prompt month use the same shape rules as the year
    // offsets: whole numbers, not before the prompt month, ordered.
    config.curveMonths = parseCurveYears(stored.monthsFrom, stored.monthsTo);
  }
  if (owns.has("dataTable")) {
    config.dataTable = parseDataTablePrefs(stored.dataTable);
  }

  return config;
}
// ── Serialising ───────────────────────────────────────────────────────────────

/**
 * The config as a saved view's stored object.
 *
 * Only the fields the scope owns, and only where they differ from the
 * default — so a view saved on the default state stores `{}` and compares
 * equal to the shipped system view. Sorted-key insertion keeps the JSON
 * stable, which matters because the dirty compare is a string compare.
 */
export function configToStored(
  scope: MarketViewScope,
  config: MarketViewConfig,
): Record<string, unknown> {
  const owns = new Set(fieldsForScope(scope));
  const out: Record<string, unknown> = {};

  // Insertion order is alphabetical to match `sortedJson`'s output; the
  // comparison sorts anyway, so this is for readable stored rows.
  if (owns.has("interval") && config.interval !== DEFAULT_CONFIG.interval) {
    out.interval = config.interval;
  }
  if (owns.has("markets") && !sameList(config.markets, DEFAULT_CONFIG.markets)) {
    out.markets = [...config.markets];
  }
  if (owns.has("seriesMode") && config.seriesMode !== DEFAULT_CONFIG.seriesMode) {
    out.seriesMode = config.seriesMode;
  }
  if (owns.has("trendYears") && config.trendYears) {
    out.trendYearsFrom = config.trendYears.from;
    out.trendYearsTo = config.trendYears.to;
  }
  if (owns.has("certificates") && config.certificates.length > 0) {
    out.certificates = [...config.certificates];
  }
  if (owns.has("compare") && config.compare.length > 0) {
    // Omitted when empty, the default — an untouched chart still stores `{}`.
    out.compare = [...config.compare];
  }
  if (owns.has("timeRange") && config.timeRange) {
    out.from = config.timeRange.from;
  }
  if (owns.has("peaks") && !sameList(config.peaks, DEFAULT_CONFIG.peaks)) {
    out.peaks = [...config.peaks];
  }
  if (owns.has("quarters") && !sameList(config.quarters, DEFAULT_CONFIG.quarters)) {
    out.quarters = [...config.quarters];
  }
  if (owns.has("range") && config.range !== DEFAULT_CONFIG.range) {
    out.range = config.range;
  }
  if (owns.has("tenors") && !sameList(config.tenors, DEFAULT_CONFIG.tenors)) {
    out.tenors = [...config.tenors];
  }
  if (owns.has("timeRange") && config.timeRange) {
    out.to = config.timeRange.to;
  }
  if (owns.has("window") && config.window !== DEFAULT_CONFIG.window) {
    out.window = config.window;
  }
  if (owns.has("years") && !sameList(config.years, DEFAULT_CONFIG.years)) {
    out.years = [...config.years];
  }
  if (owns.has("curveYears") && config.curveYears) {
    out.yearsFrom = config.curveYears.from;
    out.yearsTo = config.curveYears.to;
  }
  if (owns.has("curveMonths") && config.curveMonths) {
    out.monthsFrom = config.curveMonths.from;
    out.monthsTo = config.curveMonths.to;
  }
  if (owns.has("dataTable")) {
    // Left out when both tables are as shipped, like every other default.
    const dataTable = dataTableToStored(config.dataTable);
    if (dataTable) out.dataTable = dataTable;
  }
  if (owns.has("zones") && config.zones.length > 0) {
    // Omitted when empty, which IS the default — so an untouched capacity
    // page stores `{}` like every other surface and never boots dirty.
    out.zones = [...config.zones];
  }

  return out;
}

// ── Comparison ────────────────────────────────────────────────────────────────

function sameList<T extends number | string>(
  a: readonly T[],
  b: readonly T[],
): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/**
 * A config as a stable string, for the dirty compare.
 *
 * Keys are sorted: `configToStored` inserts in a fixed order today, but a
 * comparison that depends on insertion order is a trap waiting for the next
 * field to be added in the wrong place.
 */
export function sortedJson(stored: Record<string, unknown>): string {
  const sorted = Object.fromEntries(
    Object.entries(stored).sort(([a], [b]) => a.localeCompare(b)),
  );
  return JSON.stringify(sorted);
}

/** True when two configs describe the same state for one surface. */
export function sameConfig(
  scope: MarketViewScope,
  a: MarketViewConfig,
  b: MarketViewConfig,
): boolean {
  return sortedJson(configToStored(scope, a)) === sortedJson(configToStored(scope, b));
}

/** The scope's shipped default config, as the stored object. */
export function defaultStoredForScope(
  scope: MarketViewScope,
): Record<string, unknown> {
  return configToStored(scope, DEFAULT_CONFIG);
}

/**
 * The generated server-side default for a scope — exposed so the tests can
 * assert this file's `DEFAULT_CONFIG` against it.
 */
export function serverDefaultFor(scope: MarketViewScope): Record<string, unknown> {
  return MARKET_SYSTEM_VIEW_CONFIGS[scope] ?? {};
}

// ── Navigator window ⇄ dates ──────────────────────────────────────────────────

/**
 * The date at a fraction of a series.
 *
 * The series is the index space the brush addresses (see
 * `navigator-model.windowToLogical`), so a fraction maps to a point index
 * directly. The date part only — a brush over an intraday series still saves
 * a calendar window, which is the granularity a reader thinks in and the one
 * that survives a reload against a different set of hours.
 */
function indexAt(points: readonly SeriesPoint[], fraction: number): number {
  return Math.min(
    points.length - 1,
    Math.max(0, Math.round(fraction * (points.length - 1))),
  );
}

function dateAt(points: readonly SeriesPoint[], fraction: number): string | null {
  if (points.length === 0) return null;
  return points[indexAt(points, fraction)]?.time?.slice(0, 10) ?? null;
}

/**
 * A brush window, as the absolute dates to persist.
 *
 * `null` for the full extent (and for a series too short to address), so an
 * unbrushed chart saves no window rather than saving its current extent as if
 * the reader had chosen it.
 */
export function datesFromWindow(
  points: readonly SeriesPoint[],
  window: NavWindow,
): TimeRange | null {
  if (points.length < 2) return null;
  // The full extent is the default, not a choice — see the module comment.
  if (window.start <= 0 && window.end >= 1) return null;
  const from = dateAt(points, window.start);
  const to = dateAt(points, window.end);
  if (!from || !to || from > to) return null;
  /**
   * Test the ROUNDED INDICES, not the raw fractions.
   *
   * `dateAt` rounds a fraction to the nearest point index, so a window that
   * `sameWindow` calls a real change (its tolerance is 5e-4) can still round
   * back to the first and last points. On a 24-point hourly series that is
   * any drag from about 0.6px to 26px on a 1200px strip.
   *
   * Returning a range there persists a window identical to the default: the
   * chart snaps straight back, so the reader sees nothing move, but `dirty`
   * goes true against `DEFAULT_CONFIG` and the "Unsaved" dot survives a
   * reload with no control they can put back.
   *
   * **Indices, not the truncated dates.** An intraday series has many points
   * per calendar day, so a real window well inside a 48-hour extent still
   * truncates to the same first and last DAY — comparing dates would discard
   * it. The index pair is what the brush actually addresses.
   */
  if (indexAt(points, window.start) === 0 && indexAt(points, window.end) === points.length - 1) {
    return null;
  }
  return { from, to };
}

/**
 * Whether a series can carry a window at all.
 *
 * The brush addresses points by index, so a saved `from`/`to` is meaningless
 * against a series that has not loaded — `windowFromDates` can only answer
 * `null` there, which is indistinguishable from "no window saved".
 *
 * **This is the gate that keeps a saved window alive across a reload,** and
 * the bug it fixes is worth stating: a surface that renders its chart as
 * CONTROLLED before the data arrives hands it `null` (⇒ full extent), the
 * chart frames to that and reports it back, and the surface persists the full
 * extent — erasing the `from`/`to` the restored state had just supplied. The
 * reader sees tenor and block survive while the time range silently resets,
 * and the view flips to "Custom · Unsaved".
 *
 * So a surface persisting a window must pass `dataReady(points)` before it
 * accepts a window change. Leaving the chart UNCONTROLLED until then is not
 * enough on its own — the moment it becomes controlled it would still frame
 * from a `null` window — so the surfaces gate the commit, not the prop.
 */
export function dataReady(points: readonly SeriesPoint[]): boolean {
  return points.length >= 2;
}

/**
 * Clips a list of dated rows to the brushed window.
 *
 * **The filters drive every widget on the page, not just the chart.** A
 * surface's statistics, tables and derived panels all read their rows through
 * this, so narrowing the navigator narrows what they describe — a Summary
 * table that still reports the four-year minimum while the chart shows six
 * weeks is not a slow update, it is a wrong number, and the reader has no way
 * to tell which span any figure belongs to.
 *
 * Generic over the row so each surface can pass its own shape (a strip point,
 * a monthly index row, an hourly settlement) with an accessor for the date.
 * `null` window = the full extent, so an unbrushed page keeps every row.
 *
 * Compares on the `yyyy-MM-dd` PREFIX, which makes an hourly timestamp fall
 * inside a day-keyed window — the brush persists calendar days even on an
 * intraday series (see `datesFromWindow`), so an hour on the closing day must
 * count as inside rather than being cut by a string compare against a bare
 * date.
 */
export function clipToWindow<T>(
  rows: readonly T[],
  range: TimeRange | null,
  dateOf: (row: T) => string,
): readonly T[] {
  if (!range) return rows;
  return rows.filter((row) => {
    const day = dateOf(row).slice(0, 10);
    return day >= range.from && day <= range.to;
  });
}

/**
 * The inverse: a saved date window, as fractions of the loaded series.
 *
 * **Clamps rather than fails.** A window saved against 3 years of history and
 * re-applied with the selector on 3M has both edges outside the loaded
 * extent; the honest answer there is the widest window the data supports, not
 * an empty chart or a discarded view. `null` means "leave the brush alone",
 * which the caller reads as the full extent.
 */
export function windowFromDates(
  points: readonly SeriesPoint[],
  range: TimeRange | null,
): NavWindow | null {
  if (!range || points.length < 2) return null;

  const last = points.length - 1;
  // The first point at or after `from`, and the last at or before `to`. Index
  // arithmetic on the loaded points rather than on the calendar, because that
  // is the space the brush addresses.
  let startIndex = points.findIndex((p) => p.time.slice(0, 10) >= range.from);
  if (startIndex < 0) startIndex = last;

  let endIndex = -1;
  for (let i = last; i >= 0; i -= 1) {
    if (points[i].time.slice(0, 10) <= range.to) {
      endIndex = i;
      break;
    }
  }
  if (endIndex < 0) endIndex = 0;

  // A saved window entirely outside the loaded extent collapses; widen it to
  // the whole series rather than handing the brush an inverted range.
  if (endIndex <= startIndex) return null;

  return { start: startIndex / last, end: endIndex / last };
}

// ── Forward curve: year offsets ⇄ dates ───────────────────────────────────────

function yearOf(iso: string): number {
  return Number(iso.slice(0, 4));
}

/**
 * The calendar year a curve's year offsets count from — "this year", offset 0.
 *
 * **Derived from the curve, not a clock.** The server starts the curve at next
 * month, so the month before `points[0]` is the current one. That keeps the
 * browser's time zone out of it (a reader west of UTC would otherwise roll a
 * few hours early on the last evening of December) and works unchanged on the
 * demo data. It is not simply `points[0]`'s year: in December the curve opens
 * on January of NEXT year, and "this year" is still the one ending.
 */
export function curveBaseYear(points: readonly SeriesPoint[]): number | null {
  const first = points[0]?.time;
  if (!first) return null;
  return Number(first.slice(5, 7)) === 1 ? yearOf(first) - 1 : yearOf(first);
}

/**
 * A saved year window — a view saved while the curve's window was a year
 * slider — as the delivery months it covers on the curve as loaded.
 *
 * **Clamped to the months that exist**, so every reader of the result — the
 * chart, the grid and the Range fields — agrees. A window covering fewer than
 * two loaded months answers `null`, the whole curve, which is what
 * `windowFromDates` does with it; handing the grid the unclamped window
 * instead would filter it empty while the chart showed everything.
 */
export function rangeFromCurveYears(
  points: readonly SeriesPoint[],
  years: CurveYears | null,
): TimeRange | null {
  const base = curveBaseYear(points);
  if (!years || base === null || points.length < 2) return null;
  const inside = points.filter((p) => {
    const offset = yearOf(p.time) - base;
    return offset >= years.from && offset <= years.to;
  });
  if (inside.length < 2) return null;
  return {
    from: inside[0].time.slice(0, 10),
    to: inside[inside.length - 1].time.slice(0, 10),
  };
}

// ── Forward curve: month offsets ⇄ dates ──────────────────────────────────────

/** A `yyyy-MM` date as a running month count, for offset arithmetic. */
function monthNumber(iso: string): number {
  return Number(iso.slice(0, 4)) * 12 + Number(iso.slice(5, 7)) - 1;
}

/**
 * A saved month window, as the delivery months it covers on the curve as
 * loaded. Offsets count from the curve's first month — derived from the curve,
 * not a clock, as `curveBaseYear` is.
 *
 * Clamped to the months that exist; fewer than two answers `null`, the whole
 * curve — the same rule as `rangeFromCurveYears`, for the same reason.
 */
export function rangeFromCurveMonths(
  points: readonly SeriesPoint[],
  months: CurveMonths | null,
): TimeRange | null {
  const first = points[0]?.time;
  if (!months || !first || points.length < 2) return null;
  const base = monthNumber(first);
  const inside = points.filter((p) => {
    const offset = monthNumber(p.time) - base;
    return offset >= months.from && offset <= months.to;
  });
  if (inside.length < 2) return null;
  return {
    from: inside[0].time.slice(0, 10),
    to: inside[inside.length - 1].time.slice(0, 10),
  };
}

/**
 * The inverse: a picked month range, as the offsets a view saves.
 *
 * The whole curve saves `null` rather than today's offsets of its last month,
 * so "everything" keeps meaning everything as the far end extends, and the
 * untouched default stays `{}`.
 */
export function curveMonthsFromRange(
  points: readonly SeriesPoint[],
  range: TimeRange | null,
): CurveMonths | null {
  const first = points[0]?.time;
  const last = points[points.length - 1]?.time;
  if (!range || !first || !last) return null;
  if (range.from.slice(0, 7) <= first.slice(0, 7) && range.to.slice(0, 7) >= last.slice(0, 7)) {
    return null;
  }
  const base = monthNumber(first);
  const from = Math.max(0, monthNumber(range.from) - base);
  const to = monthNumber(range.to) - base;
  return to >= from ? { from, to } : null;
}
